import { Head, router } from "@inertiajs/react"

import { MainContainer } from "@/components/ui/container"
import { FilterChip } from "@/components/ui/filter-chip"
import { StaffLayout } from "@/components/layouts/staff-layout"
import { BranchComparison, type Comparison } from "@/features/staff/organization/branch-comparison"
import { OrganizationWorkspaceNav } from "@/features/staff/organization/workspace-nav"
import { t } from "@/lib/i18n"

/**
 * How the branches are doing, against each other.
 *
 * The server has already refused this route for a restaurant with no
 * organization, for one the platform has not granted `multi_location`, and for
 * anyone who is not a manager — so this page never renders a "you cannot see
 * this" state. There is nothing to explain to someone who was never offered it.
 *
 * **No period label under the chips**, unlike the single-branch report. That
 * label reads "del 1 al 31 de agosto" off ONE restaurant's clock, and this
 * screen spans branches whose day starts two hours apart. Printing one of their
 * clocks over all of them would be a small, confident lie; each row carries its
 * own shift count instead.
 *
 * The roster of what the group is made of lives on its own tab: it is not a
 * reading of a period, so the chips below would not govern it.
 */

type Props = {
  organization: { id: number; name: string }
  canManageOrganization: boolean
  comparison: Comparison
  presets: { value: string; label: string }[]
  preset: string
  currency: string
  currentLocationId: number
}

export default function StaffLocationOrganizationShow({
  organization,
  canManageOrganization,
  comparison,
  presets,
  preset,
  currentLocationId,
}: Props) {
  const title = t("ui.react.pages.staff.location.organization.show.page_title")

  return (
    <>
      <Head title={`${title} · ${organization.name}`} />

      <MainContainer size="md">
        <OrganizationWorkspaceNav
          current="overview"
          organizationName={organization.name}
          canManageOrganization={canManageOrganization}
        />

        {/* Wraps rather than scrolls, and takes the full 44px target on mobile,
            for the reasons `ReportPeriodBar` gives. A full page visit rather
            than nuqs state: every figure below is server-computed across N
            branches, so there is nothing the client could re-derive. */}
        <div className="flex flex-wrap gap-2" data-testid="organization-period-bar">
          {presets.map((option) => (
            <FilterChip
              key={option.value}
              active={preset === option.value}
              className="h-11 md:h-9"
              data-testid={`organization-period-option-${option.value}`}
              onClick={() => router.get("/organization", { period: option.value })}
            >
              {option.label}
            </FilterChip>
          ))}
        </div>

        <BranchComparison comparison={comparison} currentLocationId={currentLocationId} />
      </MainContainer>
    </>
  )
}


StaffLocationOrganizationShow.layout = (page: React.ReactNode) => <StaffLayout>{page}</StaffLayout>
